import { useState } from 'react';
import { jobAPI } from '../../../services/api';

// Extracted verbatim from JobManagement.js (Task 4).
//
// `documentsLoading` was already its own flag in the original code
// (separate from the shared `isLoading`), so `loadDocuments` keeps using it
// and doesn't need `setIsLoading`.
//
// Coupling note (reported, not improvised around): `loadSavedInvoices`,
// however, is a different story. In the original code it flips the SHARED
// `isLoading` flag (not `documentsLoading`) — it was never gated on the
// Documents tab, it's invoked from the invoice flow (opened from the Jobs
// tab's actions dropdown) and reads `selectedJobForActions`, then sets
// `showSavedInvoices`, both orchestrator-owned. task-4-brief.md's documented
// `useDocuments()` signature takes no arguments, but that omits what
// `loadSavedInvoices` actually needs to preserve behaviour — most
// importantly the shared `isLoading` flag this whole task is centered on
// not splitting. Rather than duplicate `isLoading` with a hook-local flag
// (which the brief explicitly warns against) or silently drop the
// `selectedJobForActions` / `setShowSavedInvoices` dependency, this hook
// takes all three as constructor arguments, following the same pattern
// already used for `setIsLoading` elsewhere in this task.
export const useDocuments = ({ setIsLoading, selectedJobForActions, setShowSavedInvoices }) => {
  const [documentsSubTab, setDocumentsSubTab] = useState('invoices');
  const [allInvoices, setAllInvoices] = useState([]);
  const [allEstimates, setAllEstimates] = useState([]);
  const [documentsPage, setDocumentsPage] = useState(1);
  const [documentsTotalPages, setDocumentsTotalPages] = useState(1);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [savedInvoices, setSavedInvoices] = useState([]);
  // #52: overlay-scoped loading, deliberately NOT the orchestrator's shared
  // `isLoading`. That flag is passed to DataTable as `loading`, so flipping it
  // swaps the table out for a loading state and unmounts the row button that
  // opened the overlay. Modal.js then has no live node to restore focus to on
  // close, and a keyboard user is dropped back to <body>. Anything that runs
  // while an overlay is open must use this instead.
  const [overlayLoading, setOverlayLoading] = useState(false);

  const loadDocuments = async () => {
    setDocumentsLoading(true);
    try {
      if (documentsSubTab === 'invoices') {
        const data = await jobAPI.getAllUserInvoices(documentsPage, 20);
        setAllInvoices(data.invoices || []);
        setDocumentsTotalPages(data.total_pages || 1);
      } else if (documentsSubTab === 'estimates') {
        const data = await jobAPI.getAllUserEstimates(documentsPage, 20);
        setAllEstimates(data.estimates || []);
        setDocumentsTotalPages(data.total_pages || 1);
      }
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      setDocumentsLoading(false);
    }
  };

  // #52: was the shared setIsLoading, which unmounted the table underneath and
  // destroyed the trigger. Overlay-scoped now, like the rest.
  const loadSavedInvoices = async () => {
    try {
      console.log('Loading saved invoices for job:', selectedJobForActions.id);
      setOverlayLoading(true);
      const result = await jobAPI.getJobInvoices(selectedJobForActions.id);
      console.log('Saved invoices result:', result);
      setSavedInvoices(result.invoices);
      setShowSavedInvoices(true);
    } catch (error) {
      console.error('Error loading saved invoices:', error);
      alert(`Error loading saved invoices: ${error.message || 'Unknown error'}`);
    } finally {
      setOverlayLoading(false);
    }
  };

  // Lifted out of the documents tab's JSX during Task 7 (reported as a finding,
  // not a rewrite). It was a ~40-line inline onClick handler that called
  // jobAPI.generateInvoicePDFWithData directly from inside the render tree —
  // exactly what the plan's "no tab calls jobAPI" rule targets. The body is
  // unchanged; the `invoice` it closed over is now a parameter.
  const downloadSavedInvoicePDF = async (invoice) => {
    try {
      // Use saved invoice data to generate PDF
      const pdfData = {
        invoice_number: invoice.invoice_number,
        job_number: invoice.job_number,
        customer_name: invoice.customer_name,
        vehicle_info: invoice.vehicle_info,
        symptoms: invoice.symptoms || '',
        parts: invoice.parts || [],
        parts_cost: parseFloat(invoice.parts_cost) || 0,
        labor_hours: parseFloat(invoice.labor_hours) || 0,
        labor_rate: parseFloat(invoice.labor_rate) || 100,
        labor_cost: parseFloat(invoice.labor_cost) || 0,
        // A saved 0% is 0%; only a missing rate falls back to 8%.
        tax_rate: Number.isNaN(parseFloat(invoice.tax_rate)) ? 0.08 : parseFloat(invoice.tax_rate),
        tax_amount: parseFloat(invoice.tax_amount) || 0,
        discount: parseFloat(invoice.discount) || 0,
        discount_type: invoice.discount_type || 'amount',
        discount_amount: parseFloat(invoice.discount_amount) || 0,
        subtotal: parseFloat(invoice.subtotal) || 0,
        total: parseFloat(invoice.total) || 0,
        status: invoice.status || 'pending',
        notes: invoice.notes || '',
        business_name: invoice.business_name || '',
        business_address: invoice.business_address || '',
        business_phone: invoice.business_phone || '',
        // The server adds type, date and approvals from invoice_number.
        vin: invoice.vin ?? null,
        license_plate: invoice.license_plate ?? null,
        odometer_in: invoice.odometer_in ?? null,
        work_description: invoice.work_description ?? null,
        return_parts: invoice.return_parts ?? null
      };

      const pdfBlob = await jobAPI.generateInvoicePDFWithData(invoice.job_id, pdfData);
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${invoice.document_type || 'invoice'}_${invoice.invoice_number}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Error downloading PDF: ' + error.message);
    }
  };

  return {
    allInvoices,
    allEstimates,
    documentsSubTab,
    setDocumentsSubTab,
    documentsPage,
    setDocumentsPage,
    documentsTotalPages,
    documentsLoading,
    loadDocuments,
    savedInvoices,
    loadSavedInvoices,
    downloadSavedInvoicePDF,
    documentsOverlayLoading: overlayLoading
  };
};
